import { useQuery } from "@tanstack/react-query";
import { suppliersApi } from "@/app/lib/api/suppliers";
import { Supplier, SupplierAddress, SupplierContact } from "@/app/lib/supabase/types";
import { useAppStore } from "@/app/lib/stores/useAppStore";

const SUPPLIER_STALE_TIME = 10 * 60 * 1000; // 10 minutes
const SUPPLIER_GC_TIME = 24 * 60 * 60 * 1000; // 24 hours

export const supplierQueryKeys = {
  all: ["suppliers"] as const,
  byCompany: (companyId: string) =>
    [...supplierQueryKeys.all, "company", companyId] as const,
  detail: (supplierId: string) =>
    [...supplierQueryKeys.all, "detail", supplierId] as const,
  addresses: (supplierId: string) =>
    [...supplierQueryKeys.all, "addresses", supplierId] as const,
  addressDetail: (addressId: string) =>
    [...supplierQueryKeys.all, "address", addressId] as const,
  contacts: (supplierId: string) =>
    [...supplierQueryKeys.all, "contacts", supplierId] as const,
  contactDetail: (contactId: string) =>
    [...supplierQueryKeys.all, "contact", contactId] as const,
  addressContacts: (addressId: string) =>
    [...supplierQueryKeys.all, "addressContacts", addressId] as const,
};

/**
 * Hook to get all suppliers for a selected company
 * @returns Supplier[]
 */
export function useSuppliers() {
  const { selectedCompanyId } = useAppStore();

  return useQuery<Supplier[]>({
    queryKey: supplierQueryKeys.byCompany(selectedCompanyId || ""),
    queryFn: () => {
      if (!selectedCompanyId) throw new Error("No company selected");
      return suppliersApi.getSuppliers(selectedCompanyId);
    },
    enabled: !!selectedCompanyId,
    staleTime: SUPPLIER_STALE_TIME,
    gcTime: SUPPLIER_GC_TIME,
  });
}

export function useSuppliersByCompany(companyId: string | undefined) {
  return useQuery<Supplier[]>({
    queryKey: supplierQueryKeys.byCompany(companyId || ""),
    queryFn: () => {
      if (!companyId) throw new Error("No company ID provided");
      return suppliersApi.getSuppliers(companyId);
    },
    enabled: !!companyId,
    staleTime: SUPPLIER_STALE_TIME,
    gcTime: SUPPLIER_GC_TIME,
  });
}

export function useSupplier(supplierId: string | undefined) {
  return useQuery<Supplier>({
    queryKey: supplierQueryKeys.detail(supplierId || ""),
    queryFn: () => {
      if (!supplierId) throw new Error("No supplier ID provided");
      return suppliersApi.getSupplier(supplierId);
    },
    enabled: !!supplierId,
    staleTime: SUPPLIER_STALE_TIME,
    gcTime: SUPPLIER_GC_TIME,
  });
}

// Supplier address hooks
export function useSupplierAddresses(supplierId: string | undefined) {
  return useQuery<SupplierAddress[]>({
    queryKey: supplierQueryKeys.addresses(supplierId || ""),
    queryFn: () => {
      if (!supplierId) throw new Error("No supplier ID provided");
      return suppliersApi.getSupplierAddresses(supplierId);
    },
    enabled: !!supplierId,
    staleTime: SUPPLIER_STALE_TIME,
    gcTime: SUPPLIER_GC_TIME,
  });
}

export function useSupplierAddress(addressId: string | undefined) {
  return useQuery<SupplierAddress>({
    queryKey: supplierQueryKeys.addressDetail(addressId || ""),
    queryFn: () => {
      if (!addressId) throw new Error("No address ID provided");
      return suppliersApi.getSupplierAddress(addressId);
    },
    enabled: !!addressId,
    staleTime: SUPPLIER_STALE_TIME,
    gcTime: SUPPLIER_GC_TIME,
  });
}

// Supplier contact hooks
export function useSupplierContacts(supplierId: string | undefined) {
  return useQuery<SupplierContact[]>({
    queryKey: supplierQueryKeys.contacts(supplierId || ""),
    queryFn: () => {
      if (!supplierId) throw new Error("No supplier ID provided");
      return suppliersApi.getSupplierContacts(supplierId);
    },
    enabled: !!supplierId,
    staleTime: SUPPLIER_STALE_TIME,
    gcTime: SUPPLIER_GC_TIME,
  });
}

export function useSupplierContact(contactId: string | undefined) {
  return useQuery<SupplierContact>({
    queryKey: supplierQueryKeys.contactDetail(contactId || ""),
    queryFn: () => {
      if (!contactId) throw new Error("No contact ID provided");
      return suppliersApi.getSupplierContact(contactId);
    },
    enabled: !!contactId,
    staleTime: SUPPLIER_STALE_TIME,
    gcTime: SUPPLIER_GC_TIME,
  });
}

// Hook to get contacts for a specific supplier address
export function useSupplierAddressContacts(addressId: string | undefined) {
  return useQuery<SupplierContact[]>({
    queryKey: supplierQueryKeys.addressContacts(addressId || ""),
    queryFn: () => {
      if (!addressId) throw new Error("No address ID provided");
      return suppliersApi.getSupplierAddressContacts(addressId);
    },
    enabled: !!addressId,
    staleTime: SUPPLIER_STALE_TIME,
    gcTime: SUPPLIER_GC_TIME,
  });
}